import React, { useState, useEffect } from 'react';
import Button from '../../../components/Button';
import { Camera, Check, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useAudience } from '../../../context/AudienceContext';
import { useAuth } from '../../../context/AuthContext';
import ParsedQuestionsPreview from '../../../components/survey/ParsedQuestionsPreview';
import useQuestionParsingSocket from '../../../hooks/useQuestionParsingSocket';
import useRealtimeQuestionTags from '../../../hooks/useRealtimeQuestionTags';
import { createSimulation, handleApiError } from '../../../utils/simulationApi';
import { ParsedQuestion } from '../../../components/survey/types';
import { isPlaceholderQuestion } from '../../../utils/questionPlaceholder';

// Info screens are display-only — they don't count toward a launchable survey
// and are stripped before submission.
const INFO_SCREEN_QUESTION_TYPE = 'info_screen';

const QUESTIONS_STILL_PARSING_ERROR =
  'Your questions are still being parsed — give it a moment, then continue.';

interface SegmentPersonaFilters {
  industryL1: string[];
  industryL2: string[];
  functions: string[];
  roles: string[];
}

// Helper to check if initial parsed questions have validation errors
const checkInitialValidationErrors = (questions: ParsedQuestion[]): boolean => {
  if (!questions || questions.length === 0) return false;

  for (const q of questions) {
    const type = q.type;

    // Choice-based questions require at least 2 choices
    if (['single_choice', 'multiple_choice', 'dropdown', 'ranking'].includes(type)) {
      if (!q.choices || q.choices.length < 2) return true;
      if (q.choices.some(c => !c || !c.trim())) return true;
    }

    // Matrix questions require at least 1 row
    if (['matrix_grid', 'matrix_rating'].includes(type)) {
      if (!q.matrix_rows || q.matrix_rows.length < 1) return true;
      if (q.matrix_rows.some(r => !r.trim())) return true;
    }

    // MaxDiff requires at least 4 items
    if (type === 'max_diff') {
      if (!q.maxdiff_items || q.maxdiff_items.length < 4) return true;
    }

    // Conjoint requires at least 2 attributes
    if (type === 'conjoint') {
      if (!q.conjoint_attributes || q.conjoint_attributes.length < 2) return true;
    }
  }

  return false;
};

interface InsightsFormProps {
  onSubmit: (simulationId: number) => void;
  selectedSegmentIds: number[];
  personaFilters: Record<number, SegmentPersonaFilters>;
  onBack: () => void;
  onEditStep?: () => void;
  initialFormData?: Record<string, any>;
  onFormDataChange?: (data: Record<string, any>) => void;
  onSavePayload?: (payload: Record<string, any>) => void;
  // Dynamic segmentation support
  dynamicSegmentationId?: number | null;
  dynamicSegmentIndices?: number[];
  /** Embedded in the marketplace: hide this form's own back button + segment
   *  badge (the marketplace owns navigation + audience). Defaults false. */
  marketplaceMode?: boolean;
}

const SurveyAndFocusGroups: React.FC<InsightsFormProps> = ({
  onSubmit,
  selectedSegmentIds,
  personaFilters,
  onBack,
  onEditStep,
  initialFormData = {},
  onFormDataChange = () => {},
  onSavePayload,
  dynamicSegmentationId,
  dynamicSegmentIndices = [],
  marketplaceMode = false,
}) => {
  const { audienceData } = useAudience();
  const { isAdmin } = useAuth();

  const [simName, setSimName] = useState(initialFormData.simName || '');
  const [goal, setGoal] = useState(initialFormData.goal || '');
  const [questionsText, setQuestionsText] = useState(initialFormData.questionsText || '');
  const [context, setContext] = useState(initialFormData.context || '');
  const [useInternalDebate, setUseInternalDebate] = useState(initialFormData.useInternalDebate || false);
  const [enableAdaptiveContext, setEnableAdaptiveContext] = useState(initialFormData.enableAdaptiveContext || false);
  const [useTwoPassParsing, setUseTwoPassParsing] = useState(initialFormData.useTwoPassParsing ?? true);
  
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [parsedQuestions, setParsedQuestions] = useState<ParsedQuestion[]>(
    initialFormData.parsedQuestions || []
  );
  const [previewDismissed, setPreviewDismissed] = useState(false);
  // Initialize with actual validation state from initial parsed questions
  const [hasValidationErrors, setHasValidationErrors] = useState(() =>
    checkInitialValidationErrors(initialFormData.parsedQuestions || [])
  );
  const [showQuestionGuide, setShowQuestionGuide] = useState(false);

  /**
   * Question images, keyed by question index.
   *
   * Server paths only, and deliberately NO File. #375 worried that the restore below
   * rebuilt each entry as `new File([], ...)` — a zero-byte placeholder behind a
   * thumbnail that looks intact — and that something downstream would upload nothing.
   * Nothing reads it: the only consumers are the two counters below and the submit path,
   * which sends `img.path`. The field was dead, so it is gone rather than left as a trap
   * for whoever wires up an upload here next.
   */
  const [questionImages, setQuestionImages] = useState<Map<number, Array<{path: string, url: string}>>>(new Map());

  // --- Celery fallback for large / image-heavy surveys ---
  const handleParsingCompleted = React.useCallback((data: any) => {
    const qs: ParsedQuestion[] = data.parsed_questions || [];
    setParsedQuestions(qs);
    setPreviewDismissed(false);
  }, []);

  const handleParsingFailed = React.useCallback((data: any) => {
    console.error('Question parsing failed:', data);
    setError(data.error || data.detail || 'Failed to parse questions');
  }, []);

  const {
    parsingState,
    triggerParsing,
    clearParsingState,
    connect
  } = useQuestionParsingSocket({
    onParsingCompleted: handleParsingCompleted,
    onParsingFailed: handleParsingFailed,
    autoConnect: false
  });

  // --- Realtime parsing (always active unless Celery is running) ---
  const {
    parsedQuestions: realtimeParsed,
    isParsing,
    isUpdating,
    error: realtimeError,
    seedCache,
    reset: resetRealtimeHook,
    loadingIndices,
  } = useRealtimeQuestionTags(
    questionsText,
    goal,
    context,
    !parsingState.isParsing,
    useTwoPassParsing,
  );

  // Apply a parse result ONLY when the hook actually produced a NEW one.
  //
  // This used to also depend on questionsText/isParsing/isUpdating, so every
  // keystroke re-ran it and re-applied the SAME (stale) realtimeParsed array on
  // top of the user's edits — silently reverting a hand-picked question type
  // before any new parse even existed. The seedCache effect below then wrote the
  // reverted array into the cache, so the re-parse faithfully preserved the
  // WRONG type. Keying on the array identity means an unchanged result is a
  // no-op.
  const lastAppliedParseRef = React.useRef<ParsedQuestion[] | null>(null);
  useEffect(() => {
    if (realtimeParsed.length > 0 && realtimeParsed !== lastAppliedParseRef.current) {
      lastAppliedParseRef.current = realtimeParsed;
      setParsedQuestions(realtimeParsed);
      setPreviewDismissed(false);
    }
  }, [realtimeParsed]);

  // Emptying the textarea clears the preview. Guarded on there being NO parse
  // result — otherwise this fires on mount (empty textarea) and wipes the result
  // the effect above just applied.
  useEffect(() => {
    if (realtimeParsed.length === 0 && !isParsing && !isUpdating && questionsText.trim() === '') {
      lastAppliedParseRef.current = null;
      setParsedQuestions([]);
    }
  }, [realtimeParsed, isParsing, isUpdating, questionsText]);

  // Surface realtime parsing errors (e.g. raw_text length cap, rate limit)
  // to the form-level error banner so respondents/authors aren't stranded
  // on a silently-stuck textarea. We track the last realtime message we
  // wrote so we only clear the banner when the user fixes the issue, never
  // when another source (Celery / submit) populated it.
  const lastRealtimeErrorRef = React.useRef<string | null>(null);
  useEffect(() => {
    if (realtimeError) {
      setError(realtimeError);
      lastRealtimeErrorRef.current = realtimeError;
      // A stream that failed mid-parse leaves Pass-1 placeholders behind. Keeping
      // the ones that did land would silently ship a shorter survey than the user
      // wrote, so the whole partial parse goes and the next edit re-parses.
      setParsedQuestions((prev) => (prev.some(isPlaceholderQuestion) ? [] : prev));
    } else if (lastRealtimeErrorRef.current) {
      const lastWritten = lastRealtimeErrorRef.current;
      lastRealtimeErrorRef.current = null;
      setError((prev) => (prev === lastWritten ? null : prev));
    }
  }, [realtimeError]);

  // Keep the hook's cache in sync with local edits so re-parses preserve them
  // Skip seeding if any questions are still loading (skeletons) to avoid cache corruption
  useEffect(() => {
    if (parsedQuestions.length > 0 && !parsedQuestions.some(isPlaceholderQuestion)) {
      seedCache(parsedQuestions);
    }
  }, [parsedQuestions, seedCache]);

  // The newest parse with no loading skeletons in it — the only version safe to hand
  // back as a draft. A Back pressed mid-parse would otherwise save the placeholders and
  // restore them as permanent empty questions, which is exactly why the cache-seed
  // effect above refuses to store them either.
  const draftQuestionsRef = React.useRef<ParsedQuestion[]>(parsedQuestions);

  // Update parent with form data changes.
  //
  // parsedQuestions is emitted because this form RESTORES it (state seed above, plus the
  // question-image restore below). Leaving it out meant a Back to change the audience
  // threw away the parse, the per-question type/choice edits and the question images —
  // the raw text survived, so it looked like the edits simply vanished.
  useEffect(() => {
    if (!parsedQuestions.some(isPlaceholderQuestion)) {
      draftQuestionsRef.current = parsedQuestions;
    }
    onFormDataChange({
      simName, goal, questionsText, context, useInternalDebate, enableAdaptiveContext, useTwoPassParsing,
      parsedQuestions: draftQuestionsRef.current,
    });
  }, [simName, goal, questionsText, context, useInternalDebate, enableAdaptiveContext, useTwoPassParsing, parsedQuestions]);

  const handleFieldChange = (
    setter: React.Dispatch<React.SetStateAction<string>>, 
    value: string
  ) => {
    setter(value);
    if (onEditStep) onEditStep();
  };

  // Initialize images from initial data (edit & rerun)
  useEffect(() => {
    if (initialFormData.parsedQuestions && Array.isArray(initialFormData.parsedQuestions)) {
      const imageMap = new Map<number, Array<{path: string, url: string}>>();
      initialFormData.parsedQuestions.forEach((question: any, index: number) => {
        if (question.image_paths?.length > 0) {
          const images = question.image_paths.map((imagePath: string) => {
            let pathForComponent = imagePath;
            if (imagePath.startsWith('http')) {
              try { pathForComponent = new URL(imagePath).pathname; } catch { /* keep original */ }
            }
            return { path: imagePath, url: pathForComponent };
          });
          imageMap.set(index, images);
        }
      });
      if (imageMap.size > 0) setQuestionImages(imageMap);
    }
  }, []);

  const saveMode = !!onSavePayload;
  const totalImageCount = Array.from(questionImages.values()).reduce((sum, imgs) => sum + imgs.length, 0);
  const questionsWithImages = questionImages.size;
  const showPreview = parsedQuestions.length > 0 && !previewDismissed;
  // Check if we have segments selected (either static or dynamic)
  const hasSegmentsSelected = selectedSegmentIds.length > 0 || dynamicSegmentIndices.length > 0;
  // Marketplace caps surveys at 15 questions (matches backend
  // MARKETPLACE_SURVEY_QUESTION_CAP). Once real-time parsing knows the count, we
  // block submit until the user trims, instead of failing at submit time.
  const MARKETPLACE_QUESTION_CAP = 15;
  const overQuestionLimit = marketplaceMode && parsedQuestions.length > MARKETPLACE_QUESTION_CAP;
  // Pass 1 of the streaming parse fills the list with placeholders and clears
  // isParsing before the real text lands, so isParsing alone leaves a window in
  // which "Loading question 1..." looks like a launchable survey (#514).
  const hasPlaceholderQuestions = parsedQuestions.some(isPlaceholderQuestion);
  // A survey needs at least one real structured question — raw text that parsed
  // to nothing (or info screens only) must not be launchable (#77).
  const structuredQuestionCount = parsedQuestions.filter(
    q => q.type !== INFO_SCREEN_QUESTION_TYPE && !isPlaceholderQuestion(q),
  ).length;
  const isFormValid = questionsText.trim() !== ''
    && structuredQuestionCount > 0
    && !hasPlaceholderQuestions
    && (saveMode || hasSegmentsSelected)
    && (!showPreview || !hasValidationErrors)
    && !overQuestionLimit;

  // Celery fallback: trigger when images attached
  const triggerCeleryFallback = async () => {
    try {
      clearParsingState();
      setError(null);
      if (!parsingState.isConnected) await connect();

      const questions = parsedQuestions.length > 0
        ? parsedQuestions.map(q => q.text)
        : [questionsText.trim()].filter(Boolean);
      if (questions.length === 0) { setError('Please enter at least one question'); return; }

      const questionTypes: Record<number, string> = {};
      parsedQuestions.forEach((q, i) => { if (q.type) questionTypes[i] = q.type; });

      const imagePaths: Record<number, string[]> = {};
      questionImages.forEach((imgs, idx) => { imagePaths[idx] = imgs.map(img => img.path); });

      await triggerParsing(
        questions, goal, context,
        Object.keys(imagePaths).length > 0 ? imagePaths : undefined,
        false,
        Object.keys(questionTypes).length > 0 ? questionTypes : undefined,
        'ai_sim',
        useTwoPassParsing,
      );
    } catch (err) {
      console.error('Error triggering Celery parsing:', err);
      setError(err instanceof Error ? err.message : 'Failed to parse questions');
    }
  };

  const handleQuestionsUpdated = (questions: ParsedQuestion[]) => {
    setParsedQuestions(questions);
    seedCache(questions);
  };

  const handleUseParsedQuestions = (questions: ParsedQuestion[]) => {
    setParsedQuestions(questions);
    seedCache(questions);
  };

  const handleBackToQuestions = () => {
    setPreviewDismissed(true);
    resetRealtimeHook();
    clearParsingState();
  };

  const handleStartSimulation = async (questions: ParsedQuestion[]) => {
    if (questions.some(isPlaceholderQuestion)) { setError(QUESTIONS_STILL_PARSING_ERROR); return; }
    const simulationData: Record<string, any> = {
      audience_id: audienceData.audienceId,
      segment_ids: selectedSegmentIds,
      name: simName,
      persona_filters: personaFilters,
      task: 'survey_and_focus_groups',
      questions: questions,
      context: context,
      goal: goal,
      use_parsed_questions: true,
      use_internal_debate: useInternalDebate,
      enable_adaptive_persona_context: enableAdaptiveContext
    };
    // Add dynamic segmentation data if present
    if (dynamicSegmentationId) {
      simulationData.dynamic_segmentation_id = dynamicSegmentationId;
      simulationData.dynamic_segment_indices = dynamicSegmentIndices;
    }
    if (onSavePayload) { onSavePayload(simulationData); return; }
    const data = await createSimulation(simulationData);
    onSubmit(data.simulation_id);
  };

  const handleSubmit = async () => {
    if (!isFormValid || isSubmitting) return;
    if (hasPlaceholderQuestions) { setError(QUESTIONS_STILL_PARSING_ERROR); return; }

    if (!showPreview) {
      if (totalImageCount > 0) {
        await triggerCeleryFallback();
      }
      return;
    }

    setIsSubmitting(true);
    try {
      const questionsForSim = parsedQuestions.filter(q => q.type !== INFO_SCREEN_QUESTION_TYPE);
      const simulationData: Record<string, any> = {
        audience_id: audienceData.audienceId,
        segment_ids: selectedSegmentIds,
        name: simName,
        persona_filters: personaFilters,
        task: 'survey_and_focus_groups',
        questions: questionsForSim,
        context: context,
        goal: goal,
        use_parsed_questions: true,
        use_internal_debate: useInternalDebate,
        enable_adaptive_persona_context: enableAdaptiveContext
      };
      // Add dynamic segmentation data if present
      if (dynamicSegmentationId) {
        simulationData.dynamic_segmentation_id = dynamicSegmentationId;
        simulationData.dynamic_segment_indices = dynamicSegmentIndices;
      }
      if (onSavePayload) { onSavePayload(simulationData); return; }
      const data = await createSimulation(simulationData);
      onSubmit(data.simulation_id);
    } catch (err) {
      console.error('Error starting simulation:', err);
      handleApiError(err, setError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="mb-6">
        {/* Back now lives beside the CTA at the foot of the form (both modes), so
            only the segment badge remains here — and that is enterprise-only. */}
        {!marketplaceMode && (
          <div className="flex justify-end items-center mb-4">
            <span className="text-sm text-gray-500">
              {dynamicSegmentIndices.length > 0
                ? `${dynamicSegmentIndices.length} dynamic segment${dynamicSegmentIndices.length !== 1 ? 's' : ''} selected`
                : `${selectedSegmentIds.length} segment${selectedSegmentIds.length !== 1 ? 's' : ''} selected`}
            </span>
          </div>
        )}
        {error && <div className="text-sm text-red-600 mb-3">{error}</div>}
      </div>

      <div className="mb-6">
        <label htmlFor="simName" className="block text-sm font-medium text-gray-700 mb-2">
          {marketplaceMode ? 'Study name' : 'Simulation Name'} <span className="text-red-500">*</span>
        </label>
        <input
          id="simName"
          type="text"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={simName}
          onChange={(e) => handleFieldChange(setSimName, e.target.value)}
          placeholder={marketplaceMode ? 'Enter a name for your study' : 'Enter a name for your simulation'}
        />
      </div>

      <div className="mb-6">
        <label htmlFor="goal" className="block text-sm font-medium text-gray-700 mb-2">
          Goal <span className="text-red-500">*</span>
        </label>
        <input
          id="goal"
          type="text"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={goal}
          onChange={(e) => handleFieldChange(setGoal, e.target.value)}
          placeholder={marketplaceMode ? 'What do you want to achieve with this study?' : 'What do you want to achieve with this simulation?'}
        />
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="questionsText" className="block text-sm font-medium text-gray-700">
            Survey or Focus Group Questions
          </label>
          <div className="flex items-center gap-3">
            {totalImageCount > 0 && (
              <span className="flex items-center text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                <Camera className="w-3 h-3 mr-1" />
                {totalImageCount} {totalImageCount === 1 ? 'image' : 'images'} across {questionsWithImages} {questionsWithImages === 1 ? 'question' : 'questions'}
              </span>
            )}
            {showPreview && !isParsing && !isUpdating && !hasPlaceholderQuestions && (
              <div className="flex items-center gap-1">
                <Check className="w-3 h-3 text-green-500" />
                <span className="text-xs text-green-600 font-medium">
                  {structuredQuestionCount} question{structuredQuestionCount !== 1 ? 's' : ''} parsed
                </span>
              </div>
            )}
            {isUpdating && !isParsing && (
              <span className="text-xs text-blue-600 font-medium">Re-parsing...</span>
            )}
          </div>
        </div>

        {/* Supported question types - compact badges */}
        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-gray-500">Supported:</span>
          {[
            { label: 'Single Choice', bg: 'bg-blue-50', text: 'text-blue-600' },
            { label: 'Multi Choice', bg: 'bg-indigo-50', text: 'text-indigo-600' },
            { label: 'Yes/No', bg: 'bg-sky-50', text: 'text-sky-600' },
            { label: 'Rating', bg: 'bg-purple-50', text: 'text-purple-600' },
            { label: 'Likert', bg: 'bg-violet-50', text: 'text-violet-600' },
            { label: 'NPS', bg: 'bg-orange-50', text: 'text-orange-600' },
            { label: 'Ranking', bg: 'bg-fuchsia-50', text: 'text-fuchsia-600' },
            { label: 'Matrix', bg: 'bg-lime-50', text: 'text-lime-600' },
            { label: 'Open Text', bg: 'bg-emerald-50', text: 'text-emerald-600' },
            { label: 'MaxDiff', bg: 'bg-rose-50', text: 'text-rose-600' },
            { label: 'Conjoint', bg: 'bg-pink-50', text: 'text-pink-600' },
          ].map(t => (
            <span key={t.label} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${t.bg} ${t.text}`}>
              {t.label}
            </span>
          ))}
        </div>

        <textarea
          id="questionsText"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          rows={6}
          value={questionsText}
          onChange={(e) => handleFieldChange(setQuestionsText, e.target.value)}
          placeholder="Type your questions freely — AI will parse them into structured survey questions automatically"
        />

        {!showPreview && !isParsing && questionsText.trim() !== '' && (
          <p className="mt-2 text-xs text-gray-500">
            Type your questions — AI will automatically split, classify, and extract options, scales, and more.
          </p>
        )}

        {/* Collapsible Question Guide */}
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setShowQuestionGuide(!showQuestionGuide)}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
          >
            <HelpCircle className="w-3.5 h-3.5" />
            <span>Question writing guide</span>
            {showQuestionGuide ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>

          {showQuestionGuide && (
            <div className="mt-2 p-4 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600 space-y-3">
              <div>
                <h4 className="font-semibold text-gray-700 mb-1.5">Tips for Better Results</h4>
                <ul className="space-y-1">
                  <li><span className="text-gray-400">•</span> <strong>Include answer options</strong> — e.g., "How satisfied? Very/Somewhat/Not at all"</li>
                  <li><span className="text-gray-400">•</span> <strong>Specify scales</strong> — e.g., "Rate 1-5" or "On a scale of 0-10"</li>
                  <li><span className="text-gray-400">•</span> <strong>Be specific</strong> — "What features do you use?" vs "Tell me about usage"</li>
                  <li><span className="text-gray-400">•</span> <strong>One question per line</strong> works best for clear separation</li>
                  <li><span className="text-gray-400">•</span> <strong>Avoid leading questions</strong> — "What do you think?" not "Don't you agree?"</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-700 mb-1.5">Example Formats</h4>
                <div className="bg-white border border-gray-200 rounded p-2 font-mono text-[11px] space-y-1">
                  <div className="text-gray-500"># Single choice with options:</div>
                  <div>How often do you exercise? Daily / Weekly / Monthly / Rarely</div>
                  <div className="text-gray-500 mt-2"># Rating scale:</div>
                  <div>Rate your satisfaction (1-5)</div>
                  <div className="text-gray-500 mt-2"># Open-ended:</div>
                  <div>What improvements would you suggest?</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Single parsing indicator for both initial parse and re-parse. It stays
            up for the whole of Pass 2 so the form never looks finished while
            placeholders are still on screen. */}
        {(isParsing || isUpdating || hasPlaceholderQuestions) && (
          <div className="mt-4 flex items-center gap-3 px-4 py-3 bg-blue-50 border border-blue-100 rounded-lg">
            <div className="flex gap-1">
              <span className="block w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '0ms', animationDuration: '1s' }} />
              <span className="block w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '150ms', animationDuration: '1s' }} />
              <span className="block w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '300ms', animationDuration: '1s' }} />
            </div>
            <span className="text-xs font-medium text-blue-700">
              {isUpdating ? 'AI is re-parsing questions...' : 'AI is parsing questions...'}
            </span>
          </div>
        )}

        {/* Celery fallback progress */}
        {parsingState.isParsing && (
          <div className="mt-3 p-3 bg-blue-50 rounded-md">
            <div className="flex items-center space-x-3">
              <div className="flex-1">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-blue-700">Parsing with images...</span>
                  <span className="text-blue-600">{parsingState.progress}%</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${parsingState.progress}%` }}
                  />
                </div>
                {parsingState.currentStep && (
                  <p className="text-xs text-blue-600 mt-1">{parsingState.currentStep}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Inline parsed questions preview -- stays visible during re-parse */}
        {showPreview && (
          <div className="mt-4">
            <ParsedQuestionsPreview
              parsedQuestions={parsedQuestions}
              onQuestionsUpdated={handleQuestionsUpdated}
              onUseQuestions={handleUseParsedQuestions}
              onStartSimulation={handleStartSimulation}
              onGoBack={handleBackToQuestions}
              onValidationChange={setHasValidationErrors}
              isLoading={false}
              saveMode={saveMode}
              loadingIndices={loadingIndices}
            />
          </div>
        )}
      </div>

      <div className="mb-6">
        <label htmlFor="context" className="block text-sm font-medium text-gray-700 mb-2">
          Context (Optional)
        </label>
        <textarea
          id="context"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          rows={3}
          value={context}
          onChange={(e) => handleFieldChange(setContext, e.target.value)}
          placeholder="Any additional context about what you're trying to learn"
        />
      </div>

      {isAdmin && (
        <>
          <div className="mb-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={useInternalDebate}
                onChange={(e) => setUseInternalDebate(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Enable Internal Debate</span>
            </label>
            <p className="mt-1 text-xs text-gray-500 ml-6">
              When enabled, each persona will go through an internal Proponent-Skeptic-Judge debate to generate more nuanced responses.
            </p>
          </div>

          <div className="mb-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={enableAdaptiveContext}
                onChange={(e) => setEnableAdaptiveContext(e.target.checked)}
                className="w-4 h-4 text-[#07E5D1] bg-gray-100 border-gray-300 rounded focus:ring-[#07E5D1] focus:ring-2"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Enable Adaptive Persona Context</span>
            </label>
            <p className="mt-1 text-xs text-gray-500 ml-6">
              Analyzes each question to identify relevant persona fields, optimizes prompts, and validates if persona data is sufficient. Shows confidence scores in results.
            </p>
          </div>

          <div className="mb-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={useTwoPassParsing}
                onChange={(e) => setUseTwoPassParsing(e.target.checked)}
                className="w-4 h-4 text-[#07E5D1] bg-gray-100 border-gray-300 rounded focus:ring-[#07E5D1] focus:ring-2"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Enable Two-Pass Parsing (Experimental)</span>
            </label>
            <p className="mt-1 text-xs text-gray-500 ml-6">
              Uses fast Haiku two-pass parsing: first detects question boundaries, then parses each in parallel for faster processing.
            </p>
          </div>
        </>
      )}

      {/* Marketplace question cap — surfaced as soon as parsing crosses the limit
          so the user trims here instead of hitting a submit-time error. */}
      {overQuestionLimit && (
        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
          This survey has {parsedQuestions.length} questions — the marketplace allows up to{' '}
          {MARKETPLACE_QUESTION_CAP}. Remove {parsedQuestions.length - MARKETPLACE_QUESTION_CAP} to continue.
        </div>
      )}

      {/* Zero structured questions — text was entered but nothing parseable
          (or only info screens), so Continue stays disabled (#77). */}
      {questionsText.trim() !== '' && structuredQuestionCount === 0
        && !isParsing && !isUpdating && !parsingState.isParsing && !hasPlaceholderQuestions && (
        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
          No survey questions detected yet — add at least one question before continuing.
        </div>
      )}

      {/* Bottom CTA -- always at the bottom of the form. Back sits beside the
          primary action so both moves live in one place, in the marketplace AND
          the main platform. */}
      <div className="flex justify-between items-center gap-3">
        <div>{error && <p className="text-sm text-red-600">{error}</p>}</div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="lg" onClick={onBack} className="whitespace-nowrap">
            Back
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting || parsingState.isParsing || isParsing}
            className="whitespace-nowrap"
          >
            {parsingState.isParsing ? 'Parsing...' : isParsing ? 'Detecting...' : hasPlaceholderQuestions ? 'Parsing questions...' : isSubmitting ? (saveMode ? 'Saving...' : 'Starting...') : saveMode ? 'Continue' : 'Start Simulation'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SurveyAndFocusGroups;
